import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { RouteBackendOverrides } from "@/api/admin-api"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"

const SMART_FOLLOWUPS_DEFAULT = "default"
const SMART_FOLLOWUPS_ENABLED = "enabled"
const SMART_FOLLOWUPS_DISABLED = "disabled"

type SmartFollowupsValue =
  | typeof SMART_FOLLOWUPS_DEFAULT
  | typeof SMART_FOLLOWUPS_ENABLED
  | typeof SMART_FOLLOWUPS_DISABLED

function smartFollowupsFromOverride(value: boolean | null | undefined): SmartFollowupsValue {
  if (value === true) return SMART_FOLLOWUPS_ENABLED
  if (value === false) return SMART_FOLLOWUPS_DISABLED
  return SMART_FOLLOWUPS_DEFAULT
}

function smartFollowupsToOverride(value: SmartFollowupsValue): boolean | null {
  if (value === SMART_FOLLOWUPS_ENABLED) return true
  if (value === SMART_FOLLOWUPS_DISABLED) return false
  return null
}

type RouteBackendOverridesDialogProps = {
  isOpen: boolean
  isLoading: boolean
  isSaving: boolean
  routeName: string
  /** Used to gate per-module fields like the slide-sense stale window. */
  routeId?: string
  overrides?: RouteBackendOverrides
  onOpenChange: (open: boolean) => void
  onSave: (overrides: RouteBackendOverrides) => Promise<void>
}

const SLIDE_SENSE_STALE_WINDOW_MIN = 1
const SLIDE_SENSE_STALE_WINDOW_MAX = 365

function parseOptionalInteger(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number(trimmed)
  if (!Number.isInteger(parsed)) return Number.NaN
  return parsed
}

export function RouteBackendOverridesDialog({
  isOpen,
  isLoading,
  isSaving,
  routeName,
  routeId,
  overrides,
  onOpenChange,
  onSave,
}: RouteBackendOverridesDialogProps) {
  const { t } = useTranslation()
  const [customInstructions, setCustomInstructions] = useState("")
  const [maxParallelSubagents, setMaxParallelSubagents] = useState("")
  const [maxIterations, setMaxIterations] = useState("")
  const [smartFollowups, setSmartFollowups] = useState<SmartFollowupsValue>(SMART_FOLLOWUPS_DEFAULT)
  const [slideSenseStaleWindow, setSlideSenseStaleWindow] = useState("")

  const isSlideSenseRoute = routeId === "slide-sense"

  useEffect(() => {
    setCustomInstructions(overrides?.custom_instructions ?? "")
    setMaxParallelSubagents(
      overrides?.explorer_deep_max_parallel_subagents != null
        ? String(overrides.explorer_deep_max_parallel_subagents)
        : ""
    )
    setMaxIterations(
      overrides?.explorer_deep_max_iterations != null
        ? String(overrides.explorer_deep_max_iterations)
        : ""
    )
    setSmartFollowups(smartFollowupsFromOverride(overrides?.explorer_deep_smart_followups))
    setSlideSenseStaleWindow(
      overrides?.slide_sense_stale_window_days != null
        ? String(overrides.slide_sense_stale_window_days)
        : ""
    )
  }, [overrides])

  const handleSubmit = async () => {
    const parsedSubagents = parseOptionalInteger(maxParallelSubagents)
    if (
      Number.isNaN(parsedSubagents) ||
      (parsedSubagents !== null && (parsedSubagents < 1 || parsedSubagents > 8))
    ) {
      toast.error(
        t("adminDashboard.routes.deepSettings.parallelLimitError", {
          defaultValue: "Max parallel subagents must be an integer from 1 to 8.",
        })
      )
      return
    }

    const parsedIterations = parseOptionalInteger(maxIterations)
    if (
      Number.isNaN(parsedIterations) ||
      (parsedIterations !== null && (parsedIterations < 1 || parsedIterations > 200))
    ) {
      toast.error(
        t("adminDashboard.routes.deepSettings.iterationLimitError", {
          defaultValue: "Max iterations must be an integer from 1 to 200.",
        })
      )
      return
    }

    let parsedStaleWindow: number | null = null
    if (isSlideSenseRoute) {
      parsedStaleWindow = parseOptionalInteger(slideSenseStaleWindow)
      if (
        Number.isNaN(parsedStaleWindow) ||
        (parsedStaleWindow !== null &&
          (parsedStaleWindow < SLIDE_SENSE_STALE_WINDOW_MIN ||
            parsedStaleWindow > SLIDE_SENSE_STALE_WINDOW_MAX))
      ) {
        toast.error(
          t("adminDashboard.routes.slideSenseSettings.staleWindowError", {
            defaultValue: `Stale window must be between ${SLIDE_SENSE_STALE_WINDOW_MIN} and ${SLIDE_SENSE_STALE_WINDOW_MAX} days.`,
            min: SLIDE_SENSE_STALE_WINDOW_MIN,
            max: SLIDE_SENSE_STALE_WINDOW_MAX,
          })
        )
        return
      }
    }

    const payload: RouteBackendOverrides = {
      custom_instructions: customInstructions,
      explorer_deep_max_parallel_subagents: parsedSubagents,
      explorer_deep_max_iterations: parsedIterations,
      explorer_deep_smart_followups: smartFollowupsToOverride(smartFollowups),
    }
    if (isSlideSenseRoute) {
      payload.slide_sense_stale_window_days = parsedStaleWindow
    }
    await onSave(payload)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>
            {t("adminDashboard.routes.deepSettings.title", {
              defaultValue: "Route backend settings",
            })}
          </DialogTitle>
          <DialogDescription>
            {t("adminDashboard.routes.deepSettings.description", {
              defaultValue: "Tune Deep Explorer runtime limits for {{routeName}}.",
              routeName,
            })}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="text-muted-foreground py-6 text-sm">
            {t("adminDashboard.common.loading", { defaultValue: "Loading..." })}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="route-custom-instructions">
                {t("adminDashboard.routes.deepSettings.customInstructions", {
                  defaultValue: "Custom instructions",
                })}
              </Label>
              <Textarea
                id="route-custom-instructions"
                value={customInstructions}
                placeholder={t("adminDashboard.routes.deepSettings.customInstructionsPlaceholder", {
                  defaultValue: "Optional backend-only instructions for this route",
                })}
                onChange={(event) => setCustomInstructions(event.target.value)}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="route-max-parallel-subagents">
                  {t("adminDashboard.routes.deepSettings.maxParallelSubagents", {
                    defaultValue: "Max parallel subagents",
                  })}
                </Label>
                <Input
                  id="route-max-parallel-subagents"
                  inputMode="numeric"
                  max={8}
                  min={1}
                  placeholder="3"
                  type="number"
                  value={maxParallelSubagents}
                  onChange={(event) => setMaxParallelSubagents(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="route-max-iterations">
                  {t("adminDashboard.routes.deepSettings.maxIterations", {
                    defaultValue: "Max iterations",
                  })}
                </Label>
                <Input
                  id="route-max-iterations"
                  inputMode="numeric"
                  max={200}
                  min={1}
                  placeholder="40"
                  type="number"
                  value={maxIterations}
                  onChange={(event) => setMaxIterations(event.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="route-smart-followups">
                {t("adminDashboard.routes.deepSettings.smartFollowups", {
                  defaultValue: "Smart followups",
                })}
              </Label>
              <Select
                value={smartFollowups}
                onValueChange={(value) => setSmartFollowups(value as SmartFollowupsValue)}
              >
                <SelectTrigger id="route-smart-followups">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SMART_FOLLOWUPS_DEFAULT}>
                    {t("adminDashboard.routes.deepSettings.smartFollowupsDefault", {
                      defaultValue: "Use Deep Explorer default",
                    })}
                  </SelectItem>
                  <SelectItem value={SMART_FOLLOWUPS_ENABLED}>
                    {t("adminDashboard.routes.deepSettings.smartFollowupsEnabled", {
                      defaultValue: "Enabled",
                    })}
                  </SelectItem>
                  <SelectItem value={SMART_FOLLOWUPS_DISABLED}>
                    {t("adminDashboard.routes.deepSettings.smartFollowupsDisabled", {
                      defaultValue: "Disabled",
                    })}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-muted-foreground text-xs">
              {t("adminDashboard.routes.deepSettings.emptyHelp", {
                defaultValue: "Leave a field empty to use the Deep Explorer default.",
              })}
            </p>

            {isSlideSenseRoute && (
              <div className="border-border/45 mt-4 space-y-2 border-t pt-4">
                <Label htmlFor="route-slide-sense-stale-window">
                  {t("adminDashboard.routes.slideSenseSettings.staleWindow", {
                    defaultValue: "Dead-items window (days)",
                  })}
                </Label>
                <Input
                  id="route-slide-sense-stale-window"
                  inputMode="numeric"
                  max={SLIDE_SENSE_STALE_WINDOW_MAX}
                  min={SLIDE_SENSE_STALE_WINDOW_MIN}
                  placeholder="90"
                  type="number"
                  value={slideSenseStaleWindow}
                  onChange={(event) => setSlideSenseStaleWindow(event.target.value)}
                />
                <p className="text-muted-foreground text-xs">
                  {t("adminDashboard.routes.slideSenseSettings.staleWindowHelp", {
                    defaultValue:
                      "Default window for the admin Dead Slides / Dead Presentations report. Leave empty to use 90 days.",
                  })}
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            disabled={isSaving}
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t("common.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button disabled={isLoading || isSaving} type="button" onClick={handleSubmit}>
            {isSaving
              ? t("common.saving", { defaultValue: "Saving..." })
              : t("common.save", { defaultValue: "Save" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
